import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sun, Moon, X, Maximize2, Play, Pause, PartyPopper, Volume2, VolumeX, FileDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmtTime = (s) => String(s || "").slice(11, 16);
const SLIDE_MS = 15000;

const downloadPdf = async () => {
  try {
    const { data } = await api.get("/election-day/scoreboard/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SECIM_SONUC_OZETI.pdf";
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail, e));
  }
};

const palette = (dark) => ({
  biz_k: "#16A34A", biz_t: dark ? "#14532D" : "#86EFAC",
  karsi_k: "#DC2626", karsi_t: dark ? "#7F1D1D" : "#FCA5A5", kura_k: "#D97706",
  bos: dark ? "#1E293B" : "#E2E8F0",
});

function SeatBar({ seats, dark }) {
  const total = seats.total || 120;
  const majority = seats.majority || 61;
  const cells = [];
  for (let i = 0; i < total; i += 1) cells.push("bos");
  let p = 0;
  for (let k = 0; k < seats.biz_confirmed; k += 1) cells[p++] = "biz_k";
  for (let k = 0; k < seats.biz_projected; k += 1) if (p < total) cells[p++] = "biz_t";
  let q = total - 1;
  for (let k = 0; k < seats.karsi_confirmed; k += 1) if (q >= p) cells[q--] = "karsi_k";
  for (let k = 0; k < seats.karsi_projected; k += 1) if (q >= p) cells[q--] = "karsi_t";
  const color = palette(dark);
  return (
    <div className="grid gap-[4px]" style={{ gridTemplateColumns: "repeat(20, minmax(0,1fr))" }}
      data-testid="scoreboard-seatmap">
      {cells.map((k, i) => {
        const maj = i + 1 === majority;
        return (
          <span key={i} className="relative aspect-square rounded-[4px]"
            data-testid={maj ? "scoreboard-majority-cell" : undefined}
            style={{
              background: color[k],
              boxShadow: maj ? "0 0 0 3px #FACC15" : undefined,
              zIndex: maj ? 2 : undefined,
            }} />
        );
      })}
    </div>
  );
}

const STATE_LABEL = {
  biz_k: "kesin bizde", biz_t: "tahmini bizde",
  karsi_k: "kesin rakip", karsi_t: "tahmini rakip", kura_k: "beraberlik · kura", bos: "belirsiz",
};

function CommitteeCard({ c, panel, fg, muted, onClose }) {
  return (
    <div className="fixed inset-0 z-[55] grid place-items-center p-5"
      style={{ background: "rgba(3,10,20,0.72)" }} onClick={onClose}
      data-testid="scoreboard-committee-card">
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: panel, color: fg }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="min-w-0">
            <p className="font-display text-2xl font-black">K{c.committee_no}</p>
            <p className="text-sm font-semibold mt-0.5">{c.name}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="scoreboard-committee-card-close"
            className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-full"
            style={{ background: "rgba(148,163,184,0.18)" }}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(148,163,184,0.12)" }}>
            <p style={{ color: muted }}>Meclis koltuğu</p>
            <p className="font-display text-xl font-bold tabular-nums">{c.seats}</p>
          </div>
          <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(148,163,184,0.12)" }}>
            <p style={{ color: muted }}>Durum</p>
            <p className="font-semibold">{STATE_LABEL[c.state]}</p>
          </div>
          <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(148,163,184,0.12)" }}>
            <p style={{ color: muted }}>Kesin sonuç (biz/rakip)</p>
            <p className="font-display text-lg font-bold tabular-nums">
              {c.our_votes ?? "-"} / {c.rival_votes ?? "-"}
              {c.invalid != null && <span className="text-xs font-normal" style={{ color: muted }}> · geçersiz {c.invalid}</span>}
            </p>
          </div>
          <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(148,163,184,0.12)" }}>
            <p style={{ color: muted }}>Canlı çetele (biz/karşı)</p>
            <p className="font-display text-lg font-bold tabular-nums">{c.biz} / {c.karsi}</p>
          </div>
          <div className="rounded-xl px-3 py-2.5 col-span-2" style={{ background: "rgba(148,163,184,0.12)" }}>
            <p style={{ color: muted }}>Katılım</p>
            <p className="font-semibold tabular-nums">
              %{c.participation} · {c.marked}/{c.faal} üye oy kullandı · belki {c.belki}
            </p>
          </div>
          <div className="rounded-xl px-3 py-2.5 col-span-2" style={{ background: "rgba(148,163,184,0.12)" }}>
            <p style={{ color: muted }}>Sandık görevlisi</p>
            <p className="font-semibold" data-testid="scoreboard-committee-officers">
              {c.officers.length ? c.officers.join(", ") : "atanmadı"}
            </p>
          </div>
        </div>
        {c.updated_at && (
          <p className="text-xs mt-3" style={{ color: muted }}>
            Sonuç girişi: {fmtTime(c.updated_at)}
          </p>
        )}
      </div>
    </div>
  );
}

function FlowChart({ flow, dark, muted }) {
  if (!flow || flow.length === 0) {
    return <p className="text-sm mt-3" style={{ color: muted }}>Henüz oy akışı yok.</p>;
  }
  const max = Math.max(...flow.map((f) => f.count), 1);
  return (
    <div className="mt-4 flex items-end gap-1.5 sm:gap-2.5" style={{ height: 180 }}
      data-testid="scoreboard-flow-chart">
      {flow.map((f) => (
        <div key={f.hour} className="flex min-w-0 flex-1 flex-col items-center justify-end h-full"
          title={`${f.hour} · ${f.count} oy · toplam ${f.cumulative}`}>
          <span className="text-[10px] sm:text-xs font-bold tabular-nums mb-1">{f.count}</span>
          <div className="w-full rounded-t-md"
            style={{ height: `${(f.count / max) * 100}%`, minHeight: 4, background: "#22C55E" }} />
          <span className="text-[10px] sm:text-xs mt-1.5 tabular-nums" style={{ color: muted }}>{f.hour}</span>
        </div>
      ))}
    </div>
  );
}

function CommitteeGrid({ rows, dark, muted, onPick }) {
  const color = palette(dark);
  const label = STATE_LABEL;
  const light = (k) => k === "biz_t" || k === "karsi_t" || k === "bos";
  return (
    <div data-testid="scoreboard-committee-grid">
      <div className="grid gap-2 sm:gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(86px, 1fr))" }}>
        {rows.map((c) => (
          <button key={c.committee_no} type="button" onClick={() => onPick(c)}
            title={`K${c.committee_no} ${c.name} — ${label[c.state]}`}
            data-testid={`scoreboard-committee-${c.committee_no}`}
            className="rounded-xl px-2 py-2.5 text-center transition-transform hover:scale-[1.05]"
            style={{
              background: color[c.state],
              color: light(c.state) && !dark ? "#0F172A" : "#FFFFFF",
              opacity: c.state === "bos" ? 0.9 : 1,
            }}>
            <p className="font-display font-black leading-none tabular-nums"
              style={{ fontSize: "clamp(18px,2vw,30px)" }}>K{c.committee_no}</p>
            <p className="text-[11px] sm:text-xs mt-1 font-semibold tabular-nums opacity-90">
              {c.seats} koltuk
            </p>
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
        {["biz_k", "biz_t", "karsi_k", "karsi_t", "bos"].map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs sm:text-sm"
            style={{ color: muted }}>
            <span className="h-3.5 w-3.5 rounded-[4px]" style={{ background: color[k] }} /> {label[k]}
          </span>
        ))}
      </div>
    </div>
  );
}

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 90 }, (_, i) => ({
    i,
    left: Math.random() * 100,
    delay: Math.random() * 1.8,
    dur: 2.6 + Math.random() * 2.4,
    size: 7 + Math.random() * 9,
    rot: Math.random() * 360,
    color: ["#22C55E", "#FACC15", "#38BDF8", "#F472B6", "#FFFFFF"][i % 5],
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <style>{`@keyframes sbFall{0%{transform:translateY(-12vh) rotate(0deg);opacity:1}
100%{transform:translateY(112vh) rotate(720deg);opacity:.9}}
@keyframes sbPop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.06);opacity:1}
100%{transform:scale(1);opacity:1}}`}</style>
      {pieces.map((p) => (
        <span key={p.i} style={{
          position: "absolute", top: 0, left: `${p.left}%`,
          width: p.size, height: p.size * 0.5, background: p.color,
          borderRadius: 2, transform: `rotate(${p.rot}deg)`,
          animation: `sbFall ${p.dur}s linear ${p.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

function Celebration({ seats, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center px-6"
      style={{ background: "rgba(3,10,20,0.92)" }} data-testid="scoreboard-celebration">
      <Confetti />
      <div className="relative text-center" style={{ animation: "sbPop .7s ease-out both" }}>
        <PartyPopper className="mx-auto h-12 w-12" style={{ color: "#FACC15" }} />
        <h2 className="font-display font-black leading-none mt-4"
          style={{ fontSize: "clamp(40px,9vw,130px)", color: "#22C55E" }}>ÇOĞUNLUK BİZDE</h2>
        <p className="font-display font-bold mt-4" style={{ fontSize: "clamp(20px,3vw,44px)", color: "#F8FAFC" }}>
          {seats.biz_total} koltuk · Rakip {seats.karsi_total}
        </p>
        <p className="text-sm sm:text-lg mt-2" style={{ color: "#94A3B8" }}>
          {seats.majority} koltuk çoğunluk sınırı aşıldı
        </p>
        <button type="button" onClick={onClose} data-testid="scoreboard-celebration-close"
          className="mt-8 rounded-full bg-white/10 px-6 py-3 text-sm font-bold text-white hover:bg-white/20">
          Kapat
        </button>
      </div>
    </div>
  );
}

export default function Scoreboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [dark, setDark] = useState(() => localStorage.getItem("sb_theme") !== "light");
  const [celebrate, setCelebrate] = useState(false);
  const celebratedRef = useRef(false);
  const [broadcast, setBroadcast] = useState(false);
  const [slide, setSlide] = useState(0);
  const [pick, setPick] = useState(null);
  const [ticker, setTicker] = useState(null);
  const [sound, setSound] = useState(() => localStorage.getItem("sb_sound") !== "off");
  const seenRef = useRef(null);
  const soundRef = useRef(sound);
  useEffect(() => {
    soundRef.current = sound;
    localStorage.setItem("sb_sound", sound ? "on" : "off");
  }, [sound]);

  const chime = useCallback(() => {
    if (!soundRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      [880, 1320].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.16);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.16 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.16 + 0.3);
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + i * 0.16);
        o.stop(ctx.currentTime + i * 0.16 + 0.32);
      });
      setTimeout(() => ctx.close(), 1200);
    } catch { /* ses engellenmişse sessiz geç */ }
  }, []);

  const watchToken = useMemo(
    () => new URLSearchParams(window.location.search).get("token"), []);
  const [sort, setSort] = useState("no");

  const load = useCallback(async () => {
    try {
      const { data } = watchToken
        ? await api.get("/public/scoreboard", { params: { token: watchToken } })
        : await api.get("/election-day/scoreboard");
      setD(data);
      setErr("");
      const keys = new Set(data.feed.map((f) => `${f.committee_no}|${f.updated_at}`));
      if (seenRef.current === null) {
        seenRef.current = keys;
      } else {
        const fresh = data.feed.filter((f) => !seenRef.current.has(`${f.committee_no}|${f.updated_at}`));
        seenRef.current = keys;
        if (fresh.length) {
          setTicker(fresh[0]);
          chime();
          setTimeout(() => setTicker(null), 12000);
        }
      }
      if (data.seats.biz_total >= data.seats.majority) {
        if (!celebratedRef.current) {
          celebratedRef.current = true;
          setCelebrate(true);
        }
      } else {
        celebratedRef.current = false;
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, e));
    }
  }, [chime, watchToken]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { localStorage.setItem("sb_theme", dark ? "dark" : "light"); }, [dark]);

  useEffect(() => {
    if (!broadcast) return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % 3), SLIDE_MS);
    return () => clearInterval(t);
  }, [broadcast]);

  useEffect(() => {
    if (!broadcast) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setSlide((s) => (s + 1) % 3);
      if (e.key === "ArrowLeft") setSlide((s) => (s + 2) % 3);
      if (e.key === "Escape") setBroadcast(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [broadcast]);

  const bg = dark ? "#07111F" : "#F8FAFC";
  const fg = dark ? "#F8FAFC" : "#0F172A";
  const muted = dark ? "#94A3B8" : "#64748B";
  const panel = dark ? "#0F1E33" : "#FFFFFF";

  if (err) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center p-8" style={{ background: bg, color: fg }}>
        <div className="text-center space-y-4" data-testid="scoreboard-error">
          <p className="text-xl font-bold">{err}</p>
          <button type="button" onClick={() => nav("/cetele")} data-testid="scoreboard-back-err"
            className="rounded-full bg-[#0F2C59] px-5 py-2 text-sm font-semibold text-white">Geri dön</button>
        </div>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center" style={{ background: bg, color: fg }}>
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const s = d.seats;
  const reached = s.biz_total >= s.majority;
  const t = d.totals;

  const critRank = (c) => (c.winner ? 999 : Math.abs(c.lead));
  const gridRows = [...d.committees].sort((a, b) => (
    sort === "kritik"
      ? critRank(a) - critRank(b) || b.seats - a.seats
      : a.committee_no - b.committee_no));

  const scoreCards = (
    <div className="grid gap-4 sm:grid-cols-3">
      <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }} data-testid="scoreboard-biz">
        <p className="text-sm font-bold tracking-widest" style={{ color: "#22C55E" }}>BİZİM LİSTE</p>
        <p className="font-display font-black leading-none tabular-nums"
          style={{ fontSize: broadcast ? "clamp(80px,16vw,260px)" : "clamp(56px,11vw,160px)", color: "#22C55E" }}>
          {s.biz_total}
        </p>
        <p className="text-sm sm:text-base mt-2" style={{ color: muted }}>
          kesin {s.biz_confirmed} · tahmin {s.biz_projected}
        </p>
      </div>
      <div className="rounded-2xl p-5 sm:p-7 flex flex-col justify-center text-center"
        style={{ background: panel }} data-testid="scoreboard-majority">
        <p className="text-sm font-bold tracking-widest" style={{ color: muted }}>ÇOĞUNLUK {s.majority}</p>
        <p className="font-display font-black leading-tight mt-2"
          style={{ fontSize: "clamp(22px,3.4vw,46px)", color: reached ? "#22C55E" : "#F59E0B" }}>
          {reached ? "ÇOĞUNLUK SAĞLANDI" : `${Math.max(s.majority - s.biz_total, 0)} KOLTUK EKSİK`}
        </p>
        <p className="text-sm sm:text-base mt-3" style={{ color: muted }}>
          Katılım %{t.participation} · {t.marked}/{t.faal} oy kullandı
        </p>
      </div>
      <div className="rounded-2xl p-5 sm:p-7 text-right" style={{ background: panel }}
        data-testid="scoreboard-karsi">
        <p className="text-sm font-bold tracking-widest" style={{ color: "#EF4444" }}>RAKİP LİSTE</p>
        <p className="font-display font-black leading-none tabular-nums"
          style={{ fontSize: broadcast ? "clamp(80px,16vw,260px)" : "clamp(56px,11vw,160px)", color: "#EF4444" }}>
          {s.karsi_total}
        </p>
        <p className="text-sm sm:text-base mt-2" style={{ color: muted }}>
          kesin {s.karsi_confirmed} · tahmin {s.karsi_projected}
        </p>
      </div>
    </div>
  );

  const seatPanel = (
    <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }}>
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <h2 className="font-display text-base md:text-lg font-bold">MECLİS DAĞILIMI</h2>
        <span className="text-sm" style={{ color: muted }}>
          {s.total} koltuk · sarı çerçeve {s.majority}. koltuk · koyu ton kesin, açık ton çetele tahmini
        </span>
        <span className="ml-auto text-sm" style={{ color: muted }}>Belirsiz {s.undecided} koltuk</span>
      </div>
      <div className="mt-5"><SeatBar seats={s} dark={dark} /></div>
    </div>
  );

  const participationPanel = (
    <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }} data-testid="scoreboard-participation">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <p className="text-sm font-bold tracking-widest" style={{ color: muted }}>CANLI KATILIM</p>
          <p className="font-display font-black leading-none tabular-nums"
            style={{ fontSize: "clamp(44px,8vw,120px)" }}>%{t.participation}</p>
        </div>
        <p className="text-sm sm:text-xl" style={{ color: muted }}>
          {t.marked}/{t.faal} üye oy kullandı · bizden {t.biz} · belki {t.belki} · karşı {t.karsi}
        </p>
      </div>
      <div className="mt-4 h-4 w-full overflow-hidden rounded-full" style={{ background: dark ? "#1E293B" : "#E2E8F0" }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(t.participation, 100)}%`, background: "#22C55E" }} />
      </div>
    </div>
  );

  const gridPanel = (
    <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }}>
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <h2 className="font-display text-base md:text-lg font-bold">KOMİTE HARİTASI</h2>
        <span className="text-sm" style={{ color: muted }}>
          {d.results_count}/{d.committees_total} komitede kesin sonuç
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {[["no", "Komite no"], ["kritik", "Kritik önce"]].map(([k, lb]) => (
            <button key={k} type="button" onClick={() => setSort(k)}
              data-testid={`scoreboard-sort-${k}`}
              className="rounded-full px-3 py-1.5 text-xs font-bold"
              style={{
                background: sort === k ? "#FACC15" : "rgba(148,163,184,0.18)",
                color: sort === k ? "#0F172A" : muted,
              }}>{lb}</button>
          ))}
        </div>
      </div>
      {sort === "kritik" && (
        <p className="text-xs mt-2" style={{ color: muted }}>
          Fark 10 ve altındaki kritik komiteler başta; farkı en küçük olan en önde.
        </p>
      )}
      <div className="mt-5"><CommitteeGrid rows={gridRows} dark={dark} muted={muted} onPick={setPick} /></div>
      <p className="text-xs mt-3" style={{ color: muted }}>
        Bir komite kutusuna dokunun: oylar, katılım ve sandık görevlisi görünür.
      </p>
    </div>
  );

  const flowPanel = (
    <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }} data-testid="scoreboard-flow">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <h2 className="font-display text-base md:text-lg font-bold">SAAT BAZLI OY AKIŞI</h2>
        <span className="text-sm" style={{ color: muted }}>
          Saat başına işaretlenen oy sayısı · toplam {t.marked}
        </span>
      </div>
      <FlowChart flow={d.flow} dark={dark} muted={muted} />
    </div>
  );

  const feedPanel = (
    <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }} data-testid="scoreboard-feed">
      <h2 className="font-display text-base md:text-lg font-bold">SON GİRİLEN KOMİTE SONUÇLARI</h2>
      {d.feed.length === 0 ? (
        <p className="text-sm mt-3" style={{ color: muted }}>Henüz kesin sonuç girilmedi.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {d.feed.map((f) => (
            <div key={f.committee_no} data-testid={`scoreboard-feed-${f.committee_no}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl px-4 py-3"
              style={{ background: dark ? "#132844" : "#F1F5F9" }}>
              <span className="font-display text-lg sm:text-2xl font-bold tabular-nums">K{f.committee_no}</span>
              <span className="text-sm sm:text-lg font-semibold truncate max-w-[42ch]">{f.name}</span>
              <span className="text-sm sm:text-lg tabular-nums" style={{ color: muted }}>
                {f.our_votes ?? "-"} / {f.rival_votes ?? "-"}
              </span>
              <span className="ml-auto rounded-full px-3 py-1 text-xs sm:text-sm font-bold text-white"
                style={{ background: f.winner === "biz" ? "#16A34A" : f.winner === "karsi" ? "#DC2626" : "#64748B" }}>
                {f.winner === "biz" ? `BİZİM LİSTE · +${f.seats}` : f.winner === "karsi" ? "RAKİP LİSTE" : "BELİRSİZ"}
              </span>
              <span className="text-xs sm:text-sm tabular-nums" style={{ color: muted }}>{fmtTime(f.updated_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const timelinePanel = (
    <div className="rounded-2xl p-5 sm:p-7" style={{ background: panel }} data-testid="scoreboard-timeline">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <h2 className="font-display text-base md:text-lg font-bold">SAATLİ ARŞİV</h2>
        <span className="text-sm" style={{ color: muted }}>
          Her saat başı kaydedilen koltuk ve katılım anlık görüntüleri
        </span>
      </div>
      {(!d.snapshots || d.snapshots.length === 0) ? (
        <p className="text-sm mt-3" style={{ color: muted }}>
          Henüz arşiv kaydı yok. İlk kayıt saat başında otomatik alınır.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="text-left" style={{ color: muted }}>
                {["Saat", "Bizde", "Rakip", "Kesin sonuç", "Katılım"].map((h) => (
                  <th key={h} className="py-2 pr-4 text-xs font-bold tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.snapshots.map((sn) => (
                <tr key={sn.at} data-testid={`scoreboard-snapshot-${sn.hour}`}
                  style={{ borderTop: `1px solid ${dark ? "#1E293B" : "#E2E8F0"}` }}>
                  <td className="py-2 pr-4 font-display text-lg font-bold tabular-nums">{sn.hour}</td>
                  <td className="py-2 pr-4 font-bold tabular-nums" style={{ color: "#22C55E" }}>{sn.biz_total}</td>
                  <td className="py-2 pr-4 font-bold tabular-nums" style={{ color: "#EF4444" }}>{sn.karsi_total}</td>
                  <td className="py-2 pr-4 tabular-nums" style={{ color: muted }}>{sn.results_count}/48</td>
                  <td className="py-2 pr-4 tabular-nums" style={{ color: muted }}>%{sn.participation} · {sn.marked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const slides = [
    <div key="s0" className="space-y-5">{scoreCards}{seatPanel}</div>,
    <div key="s1" className="space-y-5">{participationPanel}{flowPanel}{gridPanel}</div>,
    <div key="s2" className="space-y-5">{feedPanel}{timelinePanel}</div>,
  ];
  const slideNames = ["Koltuklar", "Katılım & Komiteler", "Son Sonuçlar"];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: bg, color: fg }}
      data-testid="scoreboard-page">
      {celebrate && <Celebration seats={s} onClose={() => setCelebrate(false)} />}
      {pick && <CommitteeCard c={pick} panel={panel} fg={fg} muted={muted} onClose={() => setPick(null)} />}
      {ticker && (
        <div className="sticky top-0 z-[52] -mx-0 overflow-hidden" data-testid="scoreboard-ticker"
          style={{ background: ticker.winner === "biz" ? "#16A34A" : "#DC2626" }}>
          <p className="whitespace-nowrap py-2.5 text-sm sm:text-lg font-bold text-white"
            style={{ animation: "sbTicker 14s linear 1" }}>
            YENİ KESİN SONUÇ · K{ticker.committee_no} {ticker.name} ·
            {" "}{ticker.our_votes ?? "-"} / {ticker.rival_votes ?? "-"} ·
            {" "}{ticker.winner === "biz" ? `BİZİM LİSTE KAZANDI · +${ticker.seats} koltuk` : "RAKİP LİSTE KAZANDI"}
            {" "}· {fmtTime(ticker.updated_at)}
          </p>
          <style>{`@keyframes sbTicker{0%{transform:translateX(100%)}100%{transform:translateX(-100%)}}`}</style>
        </div>
      )}
      <div className="mx-auto max-w-[1600px] px-5 py-5 sm:px-10 sm:py-8">
        <div className="flex items-start gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">
              SEÇİM SONUÇ EKRANI
            </h1>
            <p className="text-sm sm:text-base mt-1" style={{ color: muted }}>
              11 Ekim 2026 · {d.results_count}/{d.committees_total} komitede kesin sonuç ·
              {" "}son güncelleme {fmtTime(d.server_now)}
              {d.state.tally_test && <b style={{ color: "#A78BFA" }}> · PROVA/TEST</b>}
              {d.scoped_committees && (
                <b style={{ color: "#FBBF24" }} data-testid="scoreboard-scope-note">
                  {" "}· YALNIZ YETKİLİ {d.scoped_committees.length} KOMİTE
                </b>
              )}
              {broadcast && <b style={{ color: "#FACC15" }}> · YAYIN MODU: {slideNames[slide]}</b>}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={() => { setBroadcast((v) => !v); setSlide(0); }}
              data-testid="scoreboard-broadcast"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold"
              style={{ background: broadcast ? "#FACC15" : panel, color: broadcast ? "#0F172A" : fg }}
              title="Yayın modu">
              {broadcast ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              <span className="hidden sm:inline">YAYIN MODU</span>
            </button>
            {!watchToken && (
              <button type="button" data-testid="scoreboard-pdf"
                onClick={downloadPdf}
                className="grid h-10 w-10 place-items-center rounded-full"
                style={{ background: panel, color: fg }} title="Sonuç özeti PDF">
                <FileDown className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={() => setSound((v) => !v)} data-testid="scoreboard-sound"
              className="grid h-10 w-10 place-items-center rounded-full"
              style={{ background: panel, color: sound ? fg : muted }}
              title={sound ? "Sesli bildirim açık" : "Sesli bildirim kapalı"}>
              {sound ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </button>
            <button type="button" onClick={() => setDark((v) => !v)} data-testid="scoreboard-theme"
              className="grid h-10 w-10 place-items-center rounded-full"
              style={{ background: panel, color: fg }} title="Tema">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button type="button" data-testid="scoreboard-fullscreen"
              onClick={() => {
                if (document.fullscreenElement) document.exitFullscreen();
                else document.documentElement.requestFullscreen?.().catch(() => toast.error("Tam ekran açılamadı"));
              }}
              className="grid h-10 w-10 place-items-center rounded-full"
              style={{ background: panel, color: fg }} title="Tam ekran">
              <Maximize2 className="h-5 w-5" />
            </button>
            {!watchToken && (
              <button type="button" onClick={() => nav("/cetele")} data-testid="scoreboard-close"
                className="grid h-10 w-10 place-items-center rounded-full"
                style={{ background: panel, color: fg }} title="Kapat">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {broadcast ? (
          <div className="mt-6">
            {slides[slide]}
            <div className="mt-5 flex items-center justify-center gap-2" data-testid="scoreboard-slide-dots">
              {slideNames.map((n, i) => (
                <button key={n} type="button" onClick={() => setSlide(i)}
                  data-testid={`scoreboard-slide-${i}`} title={n}
                  className="h-2.5 rounded-full transition-all"
                  style={{ width: i === slide ? 40 : 14, background: i === slide ? "#FACC15" : muted }} />
              ))}
              <span className="ml-3 text-xs" style={{ color: muted }}>
                15 sn'de otomatik geçer · ← → ile elle, ESC ile çıkış
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            {scoreCards}
            {seatPanel}
            {participationPanel}
            {gridPanel}
            {feedPanel}
            {timelinePanel}
          </div>
        )}
      </div>
    </div>
  );
}
